/**
 * Shared product-card hydration helpers.
 *
 * One source of truth for the `data-bind*` directive binder used by:
 *   - `assets/athos-carousel.js` (Searchspring rails)
 *   - `assets/product-card-swap.js`     (colour-swatch swap)
 *   - `sections/collection.liquid`      (collection grid hydrator)
 *
 * Bind syntax:
 *   - `data-bind="key"`            → `el.textContent = data[key]`
 *   - `data-bind-attr="a:k|b:k2"`  → attribute assignments
 *   - `data-bind-loop="key"`       → clone inner <template> per item
 *   - `data-bind-if="key"`         → remove element when value falsy
 *
 * All bindings use `textContent` / `setAttribute` — never
 * `innerHTML` — so JSON values cannot inject markup.
 *
 * The shape consumed here matches the JSON emitted by
 * `templates/product.json.liquid` and the `_normalise()` paths in
 * the JS hydrators. The card markup is rendered by
 * `snippets/product-card.liquid` with `bind: true`.
 *
 * Loaded with `defer` ahead of every consumer in
 * `layout/theme.liquid`, so by the time any consumer's
 * `connectedCallback` / DOMContentLoaded handler runs,
 * `window.ProductCardBind` is available.
 */
(function () {
  if (window.ProductCardBind) return;

  /**
   * @param {Element} el
   * @param {string} sel
   * @returns {Element[]}
   */
  function matchSelf(el, sel) {
    const out = Array.from(el.querySelectorAll(sel));
    if (el.matches(sel)) out.unshift(el);
    return out;
  }

  /**
   * @param {object | undefined} data
   * @param {string} key
   */
  function read(data, key) {
    return data ? data[key] : undefined;
  }

  /**
   * Apply `data-bind*` directives to a cloned template subtree.
   * @param {Element} node
   * @param {object} data
   */
  function applyBindings(node, data) {
    // `data-bind-if` first so we can short-circuit subtree hydration.
    matchSelf(node, "[data-bind-if]").forEach((el) => {
      const key = el.getAttribute("data-bind-if");
      const value = read(data, key);
      if (!value || (Array.isArray(value) && value.length === 0)) {
        el.remove();
      } else {
        el.removeAttribute("data-bind-if");
      }
    });

    // Loops: clone the inner <template> once per item.
    matchSelf(node, "[data-bind-loop]").forEach((container) => {
      const key = container.getAttribute("data-bind-loop");
      const items = read(data, key) || [];
      const tpl = container.querySelector(":scope > template");
      if (!tpl) return;
      items.forEach((item) => {
        const clone = tpl.content.firstElementChild.cloneNode(true);
        applyBindings(clone, item);
        container.insertBefore(clone, tpl);
      });
      tpl.remove();
      container.removeAttribute("data-bind-loop");
    });

    // Text content.
    matchSelf(node, "[data-bind]").forEach((el) => {
      const key = el.getAttribute("data-bind");
      const value = read(data, key);
      if (value !== undefined && value !== null) el.textContent = String(value);
      el.removeAttribute("data-bind");
    });

    // Attributes (`attr:key|attr2:key2|…`).
    matchSelf(node, "[data-bind-attr]").forEach((el) => {
      const spec = el.getAttribute("data-bind-attr");
      spec.split("|").forEach((pair) => {
        const sep = pair.indexOf(":");
        if (sep === -1) return;
        const attr = pair.slice(0, sep).trim();
        const key = pair.slice(sep + 1).trim();
        if (!attr || !key) return;
        const value = read(data, key);
        if (value !== undefined && value !== null) {
          el.setAttribute(attr, String(value));
        }
      });
      el.removeAttribute("data-bind-attr");
    });
  }

  /**
   * Inject the `+N` colour-swatch overflow toggle. The bound
   * `<template>` only supports `data-bind-loop`; the toggle sits
   * outside the loop so we add it imperatively after binding.
   * No-op when there is no overflow or no swatch container.
   * @param {Element} card
   * @param {object} data
   */
  function injectColourOverflow(card, data) {
    if (!data || !data.colour_overflow || data.colour_overflow <= 0) return;
    const swatchContainer = card.querySelector('[data-js="colour-swatches"]');
    if (!swatchContainer) return;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "product-card__colour-overflow";
    btn.dataset.js = "colour-overflow-toggle";
    btn.textContent = "+" + data.colour_overflow;

    // Insert before the first overflow swatch to match server render order.
    const firstOverflow = swatchContainer.querySelector(
      '[data-overflow="true"]',
    );
    if (firstOverflow) {
      swatchContainer.insertBefore(btn, firstOverflow);
    } else {
      swatchContainer.appendChild(btn);
    }
  }

  /**
   * Clone the bound product-card template, apply bindings, and
   * inject the colour-overflow toggle. Returns null if the template
   * has no first child element.
   * @param {HTMLTemplateElement} template
   * @param {object} data
   * @returns {Element | null}
   */
  function renderCard(template, data) {
    if (!template || !template.content) return null;
    const root = template.content.firstElementChild;
    if (!root) return null;
    const card = root.cloneNode(true);
    applyBindings(card, data);
    injectColourOverflow(card, data);
    // Settle the clone's rating scaffold from the snapshot in the model;
    // without one, yotpo-ratings' MutationObserver collapses the injected
    // scaffold to the empty state (a missing snapshot means no reviews).
    if (
      data.yotpo_bottomline_raw &&
      window.YotpoRatings &&
      typeof window.YotpoRatings.prefill === "function"
    ) {
      const rating = card.querySelector('[data-js="yotpo-rating"]');
      if (rating) window.YotpoRatings.prefill(rating, data.yotpo_bottomline_raw);
    }
    card.__productData = data;
    return card;
  }

  // Document-delegated colour-swatch overflow toggle. Single
  // listener covers server-rendered, Searchspring-hydrated, and
  // swap-rendered cards.
  if (!window.__pcOverflowBound) {
    window.__pcOverflowBound = true;
    document.addEventListener("click", function (event) {
      const toggle =
        event.target.closest &&
        event.target.closest('[data-js="colour-overflow-toggle"]');
      if (!toggle) return;
      event.preventDefault();
      event.stopPropagation();
      const container = toggle.closest('[data-js="colour-swatches"]');
      if (!container) return;
      container.dataset.expanded = "true";
      // Stagger the reveal for a polished feel.
      const hidden = container.querySelectorAll(
        '.product-card__colour[data-overflow="true"]',
      );
      hidden.forEach(function (swatch, i) {
        swatch.style.transitionDelay = i * 30 + "ms";
      });
    });
  }

  /**
   * Build a single colour-swatch object for the card binder. A
   * `--custom-property` colour renders through `background: var(...)`;
   * a hex or CSS colour name rides on the `--bg-colour` variable.
   * @param {string} colour Hex, CSS colour name, or `--custom-property`.
   * @param {string} colourHandle Sibling product handle.
   * @param {string} url Sibling product url.
   * @param {boolean} isActive Whether this swatch is the current product.
   * @returns {object}
   */
  function buildSwatch(colour, colourHandle, url, isActive) {
    return {
      hex: colour,
      handle: colourHandle,
      url: url,
      style_str: colour.startsWith("--")
        ? `background: var(${colour});`
        : `--bg-colour: ${colour};`,
      active_str: isActive ? "true" : null,
      overflow_str: null,
    };
  }

  /**
   * Decode an entity-encoded, stringified swatch array into string
   * entries. Searchspring returns `ss_swatches` as a native array, but a
   * caller passing the raw metafield hands us `"[&quot;…&quot;]"`.
   * Returns [] when the value cannot be parsed.
   * @param {string} value
   * @returns {string[]}
   */
  function decodeSwatchString(value) {
    const decoded = value.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
    try {
      const parsed = JSON.parse(decoded);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    return decoded.replace(/[[\]"']/g, "").split(",");
  }

  /**
   * Extract the colour name from the first
   * `mfield_mr_custom_colour_filters` entry
   * (`"[&quot;colour|Yellow&quot;]"` → `"Yellow"`). Returns '' when absent.
   * @param {string | string[] | undefined} raw
   * @returns {string}
   */
  function extractFilterColour(raw) {
    if (!raw) return "";
    const first = Array.isArray(raw) ? raw[0] : String(raw);
    if (!first) return "";
    const decoded = first.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
    const parts = decoded.split("|");
    return parts[parts.length - 1].replace(/["'[\]]/g, "").trim();
  }

  /**
   * Parse colour swatches from a Searchspring/Athos result and normalise
   * into the shape the product-card binder expects. Preferred source is
   * `ss_swatches`: an array of `"<colour>|<handle>"` entries where
   * `<colour>` is a hex (`#BAEBFF`), a CSS colour name (`brown`), or a
   * `--custom-property`. When absent, falls back to the colour name in
   * `mfield_mr_custom_colour_filters`, then to `fallbackColour`, rendering
   * one swatch for the current product — mirroring the server-side render
   * in `snippets/product-card.liquid`. The legacy `moreColour|` prefix is
   * stripped so pre-migration entries still parse.
   * @param {object} r Searchspring/Athos result object.
   * @param {string} handle The current product handle.
   * @param {string} productUrl The current product url.
   * @param {string} [fallbackColour] Colour name fallback (e.g. from the product title).
   * @returns {{ colour_options: object[], colour_overflow: number }}
   */
  function parseColourSwatches(r, handle, productUrl, fallbackColour) {
    const attrs = r.attributes || {};
    const colourOptions = [];

    let swatchEntries = attrs.ss_swatches || r.ss_swatches || [];
    if (typeof swatchEntries === "string") {
      swatchEntries = decodeSwatchString(swatchEntries);
    }
    if (!Array.isArray(swatchEntries)) swatchEntries = [];
    if (
      swatchEntries.some(
        (entry) =>
          typeof entry === "string" &&
          (entry.indexOf("&quot;") !== -1 || entry.trim().startsWith("[")),
      )
    ) {
      swatchEntries = decodeSwatchString(swatchEntries.join(","));
    }

    swatchEntries.forEach((entry) => {
      if (typeof entry !== "string") return;
      const clean = entry.replace(/moreColour\|/g, "").trim();
      const sep = clean.indexOf("|");
      if (sep === -1) return;
      const colour = clean.slice(0, sep).trim();
      const colourHandle = clean.slice(sep + 1).trim();
      if (!colour || !colourHandle) return;
      colourOptions.push(
        buildSwatch(
          colour,
          colourHandle,
          `/products/${colourHandle}`,
          colourHandle === handle,
        ),
      );
    });

    if (colourOptions.length === 0) {
      const filterColour = extractFilterColour(
        attrs.mfield_mr_custom_colour_filters ||
          r.mfield_mr_custom_colour_filters,
      );
      const fallbackName =
        filterColour || (fallbackColour ? fallbackColour.trim() : "");
      if (fallbackName) {
        const cssColour = fallbackName.toLowerCase().replace(/\s+/g, "");
        colourOptions.push(buildSwatch(cssColour, handle, productUrl, true));
      }
    }

    for (let ci = 5; ci < colourOptions.length; ci++) {
      colourOptions[ci].overflow_str = "true";
    }

    return {
      colour_options: colourOptions,
      colour_overflow:
        colourOptions.length > 5 ? colourOptions.length - 5 : 0,
    };
  }

  window.ProductCardBind = {
    applyBindings: applyBindings,
    renderCard: renderCard,
    injectColourOverflow: injectColourOverflow,
    parseColourSwatches: parseColourSwatches,
  };
})();
